// IE Hub - Toggle Button Field Component
// Toggle button with Yes/No options

import React, { forwardRef } from 'react';
import {
  Box,
  FormHelperText,
  Typography,
  alpha,
  styled,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';

interface ValidationRules {
  required?: boolean;
  requiredMessage?: string;
}

interface ToggleButtonProps {
  label?: string | React.ReactNode;
  /** Omit or `null` = neither option selected */
  value?: boolean | null;
  onChange?: (value: boolean) => void;
  validation?: ValidationRules;
  onValidate?: (isValid: boolean, error: string) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | 'default';
  sx?: Record<string, unknown>;
  [key: string]: any;
}

// Custom styled toggle button container
const ToggleContainer = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
  backgroundColor: 'transparent',
  width: 'auto',
  '&.error': {
    borderColor: theme.palette.error.main,
  },
}));

// Custom styled toggle button
const ToggleButton = styled(Button)<{ selected?: boolean }>(({ theme, selected }) => {
  const brandColor = (theme.palette as any).brand?.main || '#155F54';

  return {
    flex: 'none',
    minWidth: '60px',
    paddingLeft: theme.spacing(1.625),
    paddingRight: theme.spacing(1.625),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderRadius: 0,
    border: 'none',
    minHeight: 'auto',
    fontSize: '16px',
    fontWeight: 400,
    textTransform: 'none',
    transition: 'all 0.2s ease-in-out',
    backgroundColor: selected ? brandColor : '#ffffff',
    color: selected ? '#ffffff' : '#000000',
    borderRight: selected ? 'none' : `1px solid ${theme.palette.divider}`,
    '&:first-of-type': {
      borderTopLeftRadius: '12px',
      borderBottomLeftRadius: '12px',
    },
    '&:last-child': {
      borderRight: 'none',
      borderTopRightRadius: '12px',
      borderBottomRightRadius: '12px',
    },
    '&:hover': {
      backgroundColor: selected
        ? alpha(brandColor, 0.9)
        : alpha(theme.palette.action.hover, 0.04),
    },
    '&:disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  };
});

const ToggleButtonField = forwardRef<HTMLDivElement, ToggleButtonProps>(
  ({
    label,
    value,
    onChange,
    validation,
    onValidate,
    error = false,
    helperText,
    disabled = false,
    size = 'medium',
    color = 'primary',
    sx = {},
    ...otherProps
  }, ref) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleToggle = (newValue: boolean) => {
      if (disabled) return;

      onChange?.(newValue);

      // Validate if validation rules exist
      if (validation?.required && newValue === undefined) {
        onValidate?.(false, validation.requiredMessage || 'This field is required');
      } else {
        onValidate?.(true, '');
      }
    };

    return (
      <Box ref={ref} sx={{ width: '100%', ...sx }} {...otherProps}>
        {label && (
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontWeight: 500,
              color: error ? 'error.main' : 'text.primary',
              fontSize: isMobile ? '14px' : '16px',
              lineHeight: isMobile ? '20px' : '22px',
            }}
          >
            {label}
          </Typography>
        )}

        <ToggleContainer className={error ? 'error' : ''}>
          <ToggleButton
            selected={value === true}
            onClick={() => handleToggle(true)}
            disabled={disabled}
            size={size}
            color={color}
          >
            Yes
          </ToggleButton>
          <ToggleButton
            selected={value === false}
            onClick={() => handleToggle(false)}
            disabled={disabled}
            size={size}
            color={color}
          >
            No
          </ToggleButton>
        </ToggleContainer>

        {helperText && (
          <FormHelperText
            error={error}
            sx={{
              mt: 0.5,
              ml: 0,
              fontSize: '0.75rem',
            }}
          >
            {helperText}
          </FormHelperText>
        )}
      </Box>
    );
  }
);

ToggleButtonField.displayName = 'ToggleButtonField';

export default ToggleButtonField;
